import React, { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { useToast } from "@/hooks/use-toast";

export interface LoggedUser {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  image?: string;
  avatar?: string;
  company?: string;
  company_logo?: string;
  license_number?: string; 
}

export interface Property {
  id: number;
  propertyListType?: { id: number; list_name: string };
  title: string;
  listing_id: string;
  address?: string | null;
  postal_code?: string | null;
  city?: string | null;
  state?: string | null;
  price?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  property_type?: string | null;
  area?: number | null;
  sqft?: number | null;
  status?: string | null;
  description?: string | null;
  text_section?: string | null;
  image?: string | null;
  original_image?: string | null;
  heroImage?: string | null;
  photos?: string[] | null;
  // Keyed by slot name (e.g. "photo2"), not array position — see updatePhotoAtIndex.
  original_photos?: Record<string, string> | null;
  custom_template_data?: Record<string, any> | null;
}

export interface PropertyList {
  id: number;
  list_name: string;
}

export interface KeyFeature {
  id?: number;
  feature_name: string;
  propetyId: number;
}

export interface FeatureItem {
  id?: number;
  feature_name: string;
}

export type FlyerData = Record<string, any>;

export function sanitizeText(value?: string | null) {
  return String(value ?? "")
    .replace(/\r?\n|\r/g, " ")
    .trim();
}

/** Treat empty strings as missing so custom tags from the property still resolve. */
export function resolvePropertyListName(
  ...candidates: (string | null | undefined)[]
): string {
  for (const candidate of candidates) {
    const trimmed = String(candidate ?? "").trim();
    if (trimmed) return trimmed;
  }
  return "";
}

/** Ensure the property's current list type appears in dropdown options. */
export function mergePropertyListsWithCurrent(
  propertyLists: PropertyList[],
  propertyListType?: { id: number; list_name: string } | null
): PropertyList[] {
  const listName = propertyListType?.list_name?.trim();
  if (!listName) return propertyLists;
  if (propertyLists.some((list) => list.list_name === listName)) {
    return propertyLists;
  }
  return [
    ...propertyLists,
    { id: propertyListType!.id, list_name: listName },
  ];
}

interface UseTemplateParams {
  property?: Property | null;
  propertyLists?: PropertyList[];
  keyFeatures?: KeyFeature[];
  selectedPropertyList?: string;
  onDirtyChange?: (d: boolean) => void;
  onChange?: (updated: Partial<FlyerData>) => void;
  onPropertyListChange?: (s: string, listId?: number) => void;
}

export function useTemplateBase({
  property,
  propertyLists = [],
  keyFeatures = [],
  selectedPropertyList: externalSelectedPropertyList,
  onDirtyChange,
  onChange,
  onPropertyListChange,
}: UseTemplateParams) {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const loggedUser: LoggedUser = JSON.parse(
    localStorage.getItem("user") || "{}"
  );
  const [selectedPropertyList, setSelectedPropertyList] = useState<string>(
    externalSelectedPropertyList ?? property?.propertyListType?.list_name ?? ""
  );

  useEffect(() => {
    if (
      externalSelectedPropertyList !== undefined &&
      externalSelectedPropertyList !== selectedPropertyList
    ) {
      setSelectedPropertyList(externalSelectedPropertyList);
    }
  }, [externalSelectedPropertyList, selectedPropertyList]);

  useEffect(() => {
    const propertyListName = property?.propertyListType?.list_name ?? "";
    if (
      propertyListName &&
      propertyListName !== selectedPropertyList &&
      !externalSelectedPropertyList
    ) {
      setSelectedPropertyList(propertyListName);
    }
  }, [property?.propertyListType?.list_name, externalSelectedPropertyList, selectedPropertyList]);

  // ✅ Helper function to get photo from array safely
  const getPhotoFromArray = useCallback(
    (photos: string[] | null | undefined, index: number): string => {
      if (!Array.isArray(photos)) return "";
      return photos[index] || "";
    },
    []
  );

  // ✅ Build initial features from backend
  const getInitialFeatures = useCallback((): FeatureItem[] => {
    if (!property) return [];
    return keyFeatures
      .filter((f) => f.propetyId === Number(property.id))
      .map((f) => ({ id: f.id, feature_name: f.feature_name }));
  }, [property?.id, keyFeatures]);

  // ✅ Build initial flyer data
  const buildFlyerData = useCallback(
    (prop: Property | null | undefined): FlyerData => {
      const photosArray = Array.isArray(prop?.photos) ? prop.photos : [];
      const features = prop
        ? keyFeatures
          .filter((f) => f.propetyId === Number(prop.id))
          .map((f) => ({ id: f.id, feature_name: f.feature_name }))
        : [];

      return {
        price: prop?.price ?? null,
        address: sanitizeText(prop?.address),
        state: prop?.state ?? "",
        city: prop?.city ?? "",
        bedrooms: prop?.bedrooms ?? null,
        bathrooms: prop?.bathrooms ?? null,
        area: prop?.area ?? prop?.sqft ?? null,
        sqft: prop?.sqft ?? prop?.area ?? null,
        description: prop?.description ?? "",
        text_section: prop?.text_section ?? "",
        headline: prop?.title ?? "",
        features,

        // ✅ Main image from 'image' field
        heroImage: prop?.image ?? "",

        // ✅ Secondary photos from 'photos' array
        photos: photosArray,
        photo2: photosArray[0] || "",
        photo3: photosArray[1] || "",
        photo4: photosArray[2] || "",
        // Keyed by slot name, not array position (see updatePhotoAtIndex). Any
        // legacy array-shaped data from before this fix is treated as empty —
        // Edit Image safely self-heals the original from the current photo.
        original_photos:
          prop?.original_photos && !Array.isArray(prop.original_photos) && typeof prop.original_photos === "object"
            ? prop.original_photos
            : {},

        agentName: loggedUser?.first_name
          ? `${loggedUser.first_name} ${loggedUser.last_name ?? ""}`.trim()
          : "",
        agentPhone: (loggedUser as any)?.phone_number ?? "",
        agentEmail: loggedUser?.email ?? "",
        // Company info (from logged user if available)
        company: (loggedUser as any)?.company ?? "",
        company_logo: (loggedUser as any)?.company_logo ?? "",
        license_number: (loggedUser as any)?.license_number ?? "", 
        custom_template_data: prop?.custom_template_data || {},
      };
    },
    [keyFeatures, loggedUser]
  );

  const [flyerData, setFlyerData] = useState<FlyerData>(() =>
    buildFlyerData(property)
  );

  const [agentData, setAgentData] = useState<LoggedUser>({});

  // ✅ Create a stable reference for photo comparison
  const prevPropertyRef = useRef<{
    id?: number;
    image?: string | null;
    heroImage?: string | null;
    photosJson?: string;
    originalPhotosJson?: string;
  }>({});

  // ✅ Track last synced features JSON representation to avoid overwriting user edits on parent re-renders
  const lastSyncedFeaturesJsonRef = useRef<string>("");

  // ✅ FIXED: useEffect that properly syncs property data
  useEffect(() => {
    if (!property) return;

    // Create comparison values
    const currentPhotosJson = JSON.stringify(property.photos || []);
    const currentOriginalPhotosJson = JSON.stringify(property.original_photos || {});
    const prev = prevPropertyRef.current;

    // Check if anything relevant changed
    const idChanged = prev.id !== property.id;
    const imageChanged = prev.image !== property.image;
    const heroImageChanged = prev.heroImage !== property.heroImage;
    const photosChanged =
      prev.photosJson !== currentPhotosJson ||
      prev.originalPhotosJson !== currentOriginalPhotosJson;

    // Update ref for next comparison
    prevPropertyRef.current = {
      id: property.id,
      image: property.image,
      heroImage: property.heroImage,
      photosJson: currentPhotosJson,
      originalPhotosJson: currentOriginalPhotosJson,
    };

    // Compute backend features and photos
    const backendFeatures = keyFeatures
      .filter((f) => f.propetyId === Number(property.id))
      .map((f) => ({ id: f.id, feature_name: f.feature_name }));

    const backendFeaturesJson = JSON.stringify(backendFeatures);
    const backendFeaturesChanged = backendFeaturesJson !== lastSyncedFeaturesJsonRef.current;

    let nextFeatures: FeatureItem[] | null = null;
    if (idChanged || backendFeaturesChanged) {
      nextFeatures = backendFeatures;
      lastSyncedFeaturesJsonRef.current = backendFeaturesJson;
    }

    const photosArray = Array.isArray(property.photos) ? property.photos : [];
    // Keyed by slot name, not array position — see updatePhotoAtIndex. Any
    // legacy array-shaped data from before this fix is treated as empty.
    const originalPhotosObject =
      property.original_photos && !Array.isArray(property.original_photos) && typeof property.original_photos === "object"
        ? property.original_photos
        : {};

    // If nothing relevant changed on the property (id/image/photos), still
    // update features when `keyFeatures` changed. To avoid overwriting user
    // edits unnecessarily, only update the features slice when it actually
    // differs from the current features.
    if (!idChanged && !imageChanged && !heroImageChanged && !photosChanged) {
      if (nextFeatures !== null) {
        setFlyerData((prev) => ({ ...prev, features: nextFeatures }));
      }
      setSelectedPropertyList(property.propertyListType?.list_name ?? "");
      return;
    }

    // Otherwise perform the normal full sync from property
    setFlyerData((prev) => {
      const newData = {
        ...prev,
        price: property.price ?? prev.price,
        address: sanitizeText(property.address) || prev.address,
        state: property.state ?? prev.state,
        city: property.city ?? prev.city,
        bedrooms: property.bedrooms ?? prev.bedrooms,
        bathrooms: property.bathrooms ?? prev.bathrooms,
        area: property.area ?? property.sqft ?? prev.area,
        sqft: property.sqft ?? property.area ?? prev.sqft,
        description: property.description ?? prev.description,
        text_section: property.text_section ?? prev.text_section,
        headline: property.title ?? prev.headline,

        // ✅ Force update images - always use property values
        heroImage: property.heroImage || property.image || "",
        photos: [...photosArray], // Create new array reference
        photo2: photosArray[0] || "",
        photo3: photosArray[1] || "",
        photo4: photosArray[2] || "",
        original_photos: { ...originalPhotosObject },

        features: nextFeatures !== null ? nextFeatures : (prev.features || []),
        custom_template_data: property.custom_template_data || {},
      };

      return newData;
    });

    setSelectedPropertyList(property.propertyListType?.list_name ?? "");
  }, [
    property?.id,
    property?.image,
    property?.photos,
    property?.original_photos,
    property?.price,
    property?.address,
    property?.bedrooms,
    property?.bathrooms,
    property?.area,
    property?.sqft,
    property?.description,
    property?.title,
    property?.propertyListType?.list_name,
    keyFeatures,
  ]);

  // ✅ Fetch agent data on mount
  useEffect(() => {
    const fetchAgentData = async () => {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axios.get(endpoints.me, {
          headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        });
        setAgentData(res.data || {});
        setFlyerData((prev) => ({
          ...prev,
          agentName: `${res.data.first_name ?? ""} ${res.data.last_name ?? ""
            }`.trim(),
          agentPhone: res.data.phone_number ?? prev.agentPhone,
          agentEmail: res.data.email ?? prev.agentEmail,
          company: res.data.company ?? prev.company,
          company_logo: res.data.company_logo ?? prev.company_logo,
          license_number: res.data.license_number ?? prev.license_number,
        }));
      } catch (err) {
        // ignore
      }
    };
    fetchAgentData();
  }, []);

  const handleInputChange = useCallback(
    (field: string, value: any) => {
      let normalized = value;
      if (field === "price") {
        normalized =
          value === "" ? null : Number(String(value).replace(/[^0-9]/g, ""));
      }
      if (["bedrooms", "bathrooms", "area", "sqft"].includes(field)) {
        normalized = value === "" ? null : Number(value);
      }

      setFlyerData((prev) => {
        const next = {
          ...prev,
          [field]: normalized,
          ...(field === "area"
            ? { sqft: normalized }
            : field === "sqft"
            ? { area: normalized }
            : {}),
        };
        onDirtyChange?.(true);
        onChange?.({
          [field]: normalized,
          ...(field === "area"
            ? { sqft: normalized }
            : field === "sqft"
            ? { area: normalized }
            : {}),
        });
        return next;
      });
    },
    [onDirtyChange, onChange]
  );

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !property?.id) {
        if (!property?.id) {
          toast({
            title: "Error",
            description:
              "No property selected. Please select a property first.",
            variant: "destructive",
          });
        }
        return;
      }

      const maxSize = 5 * 1024 * 1024;

      if (file.size > maxSize) {
        toast({
          title: "Warning",
          description: "Image size must be 5MB or smaller.",
          variant: "destructive",
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = reader.result as string;
        setFlyerData((prev) => ({ ...prev, heroImage: dataUrl }));
      };
      reader.readAsDataURL(file);

      try {
        const token = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("image", file);

        const imgRes = await axiosInstance.patch(
          `${endpoints.properties}${property.id}/`,
          formData,
          {
            headers: {
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
              "Content-Type": "multipart/form-data",
            },
          }
        );

        const imageUrl = imgRes?.data?.image || null;

        setFlyerData((prev) => {
          const next = {
            ...prev,
            heroImage: imageUrl || prev.heroImage,
          };
          onChange?.({ heroImage: next.heroImage });
          return next;
        });

        onDirtyChange?.(false);
        toast({ title: "Success", description: "Image uploaded successfully" });
      } catch (err) {
        console.error("Error saving image:", err);
        toast({
          title: "Error",
          description: "Failed to upload image to server",
          variant: "destructive",
        });
      }
    },
    [property?.id, onDirtyChange, onChange, toast]
  );

  const getCustomTemplateData = useCallback(
    (templateName: string) => {
      try {
        if (!property?.propertyListType) return null;
        return (
          (property.propertyListType as any).custom_template_data?.[
          templateName
          ] || null
        );
      } catch (err) {
        return null;
      }
    },
    [property?.propertyListType]
  );

  const saveCustomTemplateData = useCallback(
    async (templateName: string, data: any) => {
      if (!property?.propertyListType?.id) {
        toast({
          title: "Error",
          description: "No property list type selected",
          variant: "destructive",
        });
        return null;
      }

      const merged = {
        ...((property.propertyListType as any).custom_template_data || {}),
        [templateName]: data,
      };

      try {
        const token = localStorage.getItem("access_token");
        const res = await axiosInstance.patch(
          `${endpoints.propertylisttype}${property.propertyListType.id}/`,
          { custom_template_data: merged },
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );

        if (property.propertyListType) {
          (property.propertyListType as any).custom_template_data = merged;
        }

        onDirtyChange?.(false);
        return res.data;
      } catch (err) {
        console.error("Failed saving custom template data:", err);
        toast({
          title: "Error",
          description: "Failed saving custom data",
          variant: "destructive",
        });
        return null;
      }
    },
    [property?.propertyListType, onDirtyChange, toast]
  );

  const addFeature = useCallback(() => {
    setFlyerData((prev) => {
      const currentFeatures = Array.isArray(prev.features) ? prev.features : [];

      if (currentFeatures.length >= 10) {
        toast({
          title: "Feature Limit Reached",
          description: "You can add a maximum of 10 features per property.",
          variant: "destructive",
        });
        return prev;
      }

      const newFeature = { feature_name: "" };
      const features = [...currentFeatures, newFeature];

      onDirtyChange?.(true);
      return { ...prev, features };
    });
  }, [onDirtyChange, toast]);

  const handleFeatureChange = useCallback(
    (index: number, value: string) => {
      setFlyerData((prev) => {
        const currentFeatures = Array.isArray(prev.features)
          ? [...prev.features]
          : [];

        while (currentFeatures.length <= index) {
          currentFeatures.push({ feature_name: "" });
        }

        currentFeatures[index] = {
          ...(currentFeatures[index] || {}),
          feature_name: value,
        };

        if (value.trim()) {
          onDirtyChange?.(true);
        }

        return { ...prev, features: currentFeatures };
      });
    },
    [onDirtyChange]
  );

  const handleFeatureBlur = useCallback(
    (index: number) => {
      setFlyerData((prev) => {
        const currentFeatures = Array.isArray(prev.features)
          ? [...prev.features]
          : [];
        const feature = currentFeatures[index];

        if (!feature?.feature_name?.trim() && !feature?.id) {
          const features = currentFeatures.filter((_, i) => i !== index);
          return { ...prev, features };
        }

        if (feature?.feature_name?.trim()) {
          onChange?.({ features: currentFeatures });
        }

        return prev;
      });
    },
    [onChange]
  );

  const handleFeatureDelete = useCallback(
    (index: number) => {
      setFlyerData((prev) => {
        const currentFeatures = Array.isArray(prev.features)
          ? [...prev.features]
          : [];
        const featureToDelete = currentFeatures[index];
        const features = currentFeatures.filter((_, i) => i !== index);

        const hadContent =
          featureToDelete?.id || featureToDelete?.feature_name?.trim();

        if (hadContent) {
          onDirtyChange?.(true);
          onChange?.({ features });
        }

        return { ...prev, features };
      });
    },
    [onDirtyChange, onChange]
  );

  // Persistence for the property's list type (e.g. "Just Listed", "Just Added") lives in a
  // single place — the parent's onPropertyListChange handler — to avoid firing multiple
  // concurrent PATCH requests to the same property that can race and clobber each other.
  const updatePropertyList = useCallback(
    (newListName: string, listId?: number) => {
      // Update internal state immediately for instant UI feedback
      setSelectedPropertyList(newListName);
      onDirtyChange?.(true);
      onPropertyListChange?.(newListName, listId);
      // ✅ Also notify through onChange for template sync
      onChange?.({ list_name: newListName });
    },
    [onDirtyChange, onPropertyListChange, onChange]
  );

  const updatePhotoAtIndex = useCallback(
    async (
      imageUrl: string,
      photoSlotName: "photo2" | "photo3" | "photo4",
      options?: { captureOriginal?: boolean }
    ) => {
      // captureOriginal defaults to true: a fresh photo pick becomes its own
      // untouched "original" so Edit Image can always re-crop from the full
      // source later. The new crop-save flow passes captureOriginal: false
      // so re-cropping an already-cropped photo doesn't overwrite the
      // original with the crop.
      const captureOriginal = options?.captureOriginal ?? true;
      const indexMap: Record<"photo2" | "photo3" | "photo4", number> = {
        photo2: 0,
        photo3: 1,
        photo4: 2,
      };
      const targetIndex = indexMap[photoSlotName];

      setFlyerData((prev) => {
        const currentPhotos = Array.isArray(prev.photos)
          ? [...prev.photos]
          : [];

        while (currentPhotos.length <= targetIndex) {
          currentPhotos.push("");
        }

        currentPhotos[targetIndex] = imageUrl;

        const next: FlyerData = {
          ...prev,
          [photoSlotName]: imageUrl,
          photos: currentPhotos,
          photo2: currentPhotos[0] || "",
          photo3: currentPhotos[1] || "",
          photo4: currentPhotos[2] || "",
        };

        if (captureOriginal) {
          // Keyed by slot name (not array position) — a fresh upload becomes
          // its own original under that slot's own key, with no index
          // arithmetic and no risk of shifting another slot's original.
          const currentOriginalPhotos =
            prev.original_photos && typeof prev.original_photos === "object" && !Array.isArray(prev.original_photos)
              ? { ...prev.original_photos }
              : {};
          currentOriginalPhotos[photoSlotName] = imageUrl;
          next.original_photos = currentOriginalPhotos;
        }

        onDirtyChange?.(true);
        onChange?.({
          [photoSlotName]: imageUrl,
          photos: currentPhotos,
        });

        return next;
      });

      if (property?.id) {
        try {
          const token = localStorage.getItem("access_token");

          // Get current photos and update
          const currentFlyerPhotos = flyerData.photos || [];
          const photosToSave = [...currentFlyerPhotos];
          while (photosToSave.length <= targetIndex) {
            photosToSave.push("");
          }
          photosToSave[targetIndex] = imageUrl;

          const filteredPhotos = photosToSave.filter(
            (p) => p && p.trim() !== ""
          );

          const patchBody: Record<string, any> = { photos: filteredPhotos };

          if (captureOriginal) {
            const currentFlyerOriginalPhotos =
              flyerData.original_photos && typeof flyerData.original_photos === "object" && !Array.isArray(flyerData.original_photos)
                ? { ...flyerData.original_photos }
                : {};
            currentFlyerOriginalPhotos[photoSlotName] = imageUrl;
            patchBody.original_photos = currentFlyerOriginalPhotos;
          }

          await axiosInstance.patch(
            `${endpoints.properties}${property.id}/`,
            patchBody,
            {
              headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            }
          );

          onDirtyChange?.(false);
        } catch (err) {
          console.error("Failed to save photos to backend:", err);
          toast({
            title: "Error",
            description: "Failed to save photo to property",
            variant: "destructive",
          });
        }
      }
    },
    [property?.id, flyerData.photos, flyerData.original_photos, onDirtyChange, onChange, toast]
  );

  const removePhotoAtIndex = useCallback(
    async (photoSlotName: "photo2" | "photo3" | "photo4") => {
      const indexMap: Record<"photo2" | "photo3" | "photo4", number> = {
        photo2: 0,
        photo3: 1,
        photo4: 2,
      };
      const targetIndex = indexMap[photoSlotName];

      setFlyerData((prev) => {
        const currentPhotos = Array.isArray(prev.photos)
          ? [...prev.photos]
          : [];

        if (currentPhotos.length > targetIndex) {
          currentPhotos[targetIndex] = "";
        }

        const currentOriginalPhotos =
          prev.original_photos && typeof prev.original_photos === "object" && !Array.isArray(prev.original_photos)
            ? { ...prev.original_photos }
            : {};
        delete currentOriginalPhotos[photoSlotName];

        const next = {
          ...prev,
          [photoSlotName]: "",
          photos: currentPhotos,
          photo2: currentPhotos[0] || "",
          photo3: currentPhotos[1] || "",
          photo4: currentPhotos[2] || "",
          original_photos: currentOriginalPhotos,
        };

        onDirtyChange?.(true);
        onChange?.({
          [photoSlotName]: "",
          photos: currentPhotos,
        });

        return next;
      });

      if (property?.id) {
        try {
          const token = localStorage.getItem("access_token");

          const currentFlyerPhotos = flyerData.photos || [];
          const photosToSave = [...currentFlyerPhotos];
          if (photosToSave.length > targetIndex) {
            photosToSave[targetIndex] = "";
          }

          const filteredPhotos = photosToSave.filter(
            (p) => p && p.trim() !== ""
          );

          const originalPhotosToSave =
            flyerData.original_photos && typeof flyerData.original_photos === "object" && !Array.isArray(flyerData.original_photos)
              ? { ...flyerData.original_photos }
              : {};
          delete originalPhotosToSave[photoSlotName];

          await axiosInstance.patch(
            `${endpoints.properties}${property.id}/`,
            { photos: filteredPhotos, original_photos: originalPhotosToSave },
            {
              headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            }
          );

          onDirtyChange?.(false);
        } catch (err) {
          console.error("Failed to remove photo from backend:", err);
          toast({
            title: "Error",
            description: "Failed to remove photo from property",
            variant: "destructive",
          });
        }
      }
    },
    [property?.id, flyerData.photos, flyerData.original_photos, onDirtyChange, onChange, toast]
  );

  // Converts a crop modal's output data URL into an uploaded photo URL, the
  // same way a freshly-picked secondary photo file already gets uploaded
  // (via endpoints.mls_uploads). Used by each template's secondary-photo
  // "Edit Image" confirm handler so the cropped result becomes a real URL
  // that updatePhotoAtIndex can store, instead of a giant inline data URL.
  const uploadCroppedPhoto = useCallback(
    async (dataUrl: string): Promise<string> => {
      const arr = dataUrl.split(",");
      const mimeMatch = arr[0].match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : "image/png";
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      const file = new File([u8arr], `edited-photo-${Date.now()}.png`, {
        type: mime,
      });

      const token = localStorage.getItem("access_token");
      const formData = new FormData();
      formData.append("image", file);

      const res = await axiosInstance.post(endpoints.mls_uploads, formData, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          "Content-Type": "multipart/form-data",
        },
      });

      if (!res?.data?.url) {
        throw new Error("No URL returned from upload");
      }
      return res.data.url;
    },
    []
  );

  // ✅ Force refresh function for external use
  const forceRefresh = useCallback(() => {
    if (property) {
      setFlyerData(buildFlyerData(property));
    }
  }, [property, buildFlyerData]);

  return {
    flyerData,
    setFlyerData,
    selectedPropertyList,
    setSelectedPropertyList,
    fileInputRef,
    agentData,
    handleInputChange,
    handleFileChange,
    addFeature,
    handleFeatureChange,
    handleFeatureBlur,
    handleFeatureDelete,
    updatePropertyList,
    updatePhotoAtIndex,
    removePhotoAtIndex,
    uploadCroppedPhoto,
    toast,
    property,
    getCustomTemplateData,
    saveCustomTemplateData,
    forceRefresh, // ✅ Export for external use
  };
}

export default useTemplateBase;
